import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("tm_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("tm_token");
    if (!token) return null;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("tm_user", JSON.stringify(data));
      return data;
    } catch (e) {
      console.warn("[Auth] refreshUser failed:", e?.response?.status || e?.message);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("tm_token");
    if (token) refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (phone, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { phone, password });
      localStorage.setItem("tm_token", data.token);
      localStorage.setItem("tm_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const register = useCallback(async (phone, password, name, promoterCode) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        phone, password, name,
        promoter_code: promoterCode || null,
      });
      localStorage.setItem("tm_token", data.token);
      localStorage.setItem("tm_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("tm_token");
    localStorage.removeItem("tm_user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, refreshUser, loading }),
    [user, login, register, logout, refreshUser, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
